<template>
  <div class="register-toolbar">
    <div class="register-toolbar-bar">
      <div class="register-toolbar-dates">
        <div class="register-toolbar-field">
          <label class="register-toolbar-label">From</label>
          <input
              v-model="start"
              @change="updateDateQuery"
              type="date"
              class="form-control register-toolbar-date"
          >
        </div>
        <div class="register-toolbar-field">
          <label class="register-toolbar-label">To</label>
          <input
              v-model="end"
              @change="updateDateQuery"
              type="date"
              class="form-control register-toolbar-date"
          >
        </div>
        <button type="button" class="btn btn-primary register-toolbar-go" @click="onClickGo">Go</button>
      </div>
      <div class="register-toolbar-actions">
        <button type="button" class="btn register-toolbar-btn" @click="emit('print')">
          <i class="fa-solid fa-print"></i>
          Print
        </button>
        <div v-if="exportOptions.length" class="dropdown">
          <button
              type="button"
              class="btn register-toolbar-btn"
              data-bs-toggle="dropdown"
              aria-expanded="false"
          >
            <i class="fa-regular fa-file-excel"></i>
            Export
            <i class="fa-solid fa-chevron-down register-toolbar-caret"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li v-for="option in exportOptions" :key="option.key">
              <a class="dropdown-item" href="#" @click.prevent="emit('export', option.key)">{{ option.label }}</a>
            </li>
          </ul>
        </div>
        <button v-else type="button" class="btn register-toolbar-btn" @click="emit('export')">
          <i class="fa-regular fa-file-excel"></i>
          Export
        </button>
        <FilterPopover
            ref="filterPopoverRef"
            title="Filters"
            :active-count="activeChips.length"
            :popover-width="popoverWidth"
            @open="emit('open-filter')"
            @apply="emit('apply')"
            @clear="emit('clear')"
        >
          <slot></slot>
        </FilterPopover>
      </div>
    </div>
    <div v-if="activeChips.length" class="register-chips">
      <span v-for="chip in activeChips" :key="chip.key" class="register-chip">
        <span class="register-chip-label">{{ chip.label }}:</span>
        {{ chip.text }}
        <button type="button" class="register-chip-remove" @click="emit('remove-chip', chip.key)">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </span>
      <button type="button" class="register-chip-clear" @click="emit('clear')">Clear all</button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FilterPopover from '@/components/atom/FilterPopover.vue'

const props = defineProps({
  filters: {
    type: Object,
    required: true
  },
  chipMeta: {
    type: Array,
    default: () => []
  },
  popoverWidth: {
    type: String,
    default: '560px'
  },
  exportOptions: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['go', 'open-filter', 'apply', 'clear', 'remove-chip', 'print', 'export'])

const route = useRoute()
const router = useRouter()

const filterPopoverRef = ref(null)
const start = ref(route.query.start || '')
const end = ref(route.query.end || '')

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0
  return !! value
}

const getChipText = (value, textKey) => {
  if (Array.isArray(value)) return value.map(option => option[textKey]).join(', ')
  return value[textKey]
}

const activeChips = computed(() => {
  return props.chipMeta.filter(meta => hasValue(props.filters[meta.key])).map(meta => {
    return {
      key: meta.key,
      label: meta.label,
      text: getChipText(props.filters[meta.key], meta.text_key)
    }
  })
})

const buildDateQuery = () => {
  const routeQuery = Object.assign({}, route.query, { start: start.value, end: end.value })
  if (! start.value) delete routeQuery.start
  if (! end.value) delete routeQuery.end
  return routeQuery
}

const updateDateQuery = async () => {
  await router.push({ path: route.path, query: buildDateQuery() })
}

const onClickGo = async () => {
  await updateDateQuery()
  emit('go')
}

const openFilter = () => {
  if (! filterPopoverRef.value) return
  filterPopoverRef.value.open()
}

defineExpose({
  openFilter
})
</script>

<style scoped>
.register-toolbar {
  margin-bottom: 16px;
}
.register-toolbar-bar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.register-toolbar-dates {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.register-toolbar-field {
  display: flex;
  flex-direction: column;
}
.register-toolbar-label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #101828;
}
.register-toolbar-date {
  width: 170px;
  border-radius: 8px;
  border-color: #e4e7ec;
}
.register-toolbar-go {
  min-width: 72px;
  border-radius: 8px;
}
.register-toolbar-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-left: auto;
}
.register-toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
  color: #475467;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.register-toolbar-btn:hover {
  background: #f9fafb;
  color: #101828;
}
.register-toolbar-btn.show {
  border-color: #7367f0;
  color: #7367f0;
}
.register-toolbar-caret {
  margin-left: 2px;
  font-size: 10px;
  transition: transform .15s ease;
}
.register-toolbar-btn.show .register-toolbar-caret {
  transform: rotate(180deg);
}
.register-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.register-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 10px;
  border-radius: 16px;
  background: #f4f3ff;
  color: #4338ca;
  font-size: 12px;
}
.register-chip-label {
  color: #7c78a8;
}
.register-chip-remove {
  border: 0;
  background: transparent;
  color: #7c78a8;
  font-size: 11px;
  line-height: 1;
  padding: 2px;
  cursor: pointer;
}
.register-chip-remove:hover {
  color: #4338ca;
}
.register-chip-clear {
  border: 0;
  background: transparent;
  color: #667085;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}
.register-toolbar :deep(.filter-search-icon) {
  margin-bottom: 4px;
  padding: 0 2px;
  font-size: 12px;
  color: #98a2b3;
  cursor: pointer;
}
.register-toolbar :deep(.filter-search-icon:hover) {
  color: #7367f0;
}
@media (max-width: 576px) {
  .register-toolbar-dates {
    width: 100%;
  }
  .register-toolbar-field {
    flex: 1 1 140px;
  }
  .register-toolbar-date {
    width: 100%;
  }
}
</style>
